import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Gauge, Users, Building2, ListChecks, Brain, CalendarDays, Ticket, Handshake,
  ChevronDown, ChevronRight, LogOut, Menu, X, UserSearch, Fingerprint,
  UserCheck, Atom, FileSearch, Scale, Anchor, Landmark, Store, Gavel,
  Sun, Moon, Rocket, BookOpen, PlayCircle, Archive, FileBarChart2, Library, GraduationCap,
  BrainCircuit, Kanban, ExternalLink, Headphones, Target, UsersRound, Radar, FileText, Briefcase, AlertOctagon, Globe, ShieldCheck
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { useServices } from "../context/ServicesContext";
import MoonstoneWordmark from "./MoonstoneWordmark";

const menuItems = [
  {
    id: "dashboard", labelKey: "menu.dashboard", icon: Gauge, path: "/dashboard"
  },
  {
    id: "hoi", labelKey: "menu.house_of_intelligence", icon: BrainCircuit, path: "/house-of-intelligence"
  },
  {
    id: "intelligence", labelKey: "menu.intelligence", icon: Fingerprint,
    children: [
      { labelKey: "menu.people", path: "/sales/profiles", icon: UserSearch },
      { labelKey: "menu.company", path: "/sales/businesses", icon: Building2 },
      { labelKey: "menu.people_list", path: "/sales/people-list", icon: UsersRound },
      { labelKey: "menu.list", path: "/sales/list", icon: ListChecks },
      { labelKey: "menu.b2b_fairs", path: "/network/tradeshow", icon: Ticket },
      { labelKey: "menu.b2b_events", path: "/network/events", icon: Handshake },
      { labelKey: "menu.business_asset", path: "/tech/monitor", icon: Atom },
      { labelKey: "menu.regulatory_affairs", path: "/compliance", icon: Scale },
    ]
  },
  {
    id: "warroom", labelKey: "menu.war_room", icon: Radar, path: "/warroom"
  },
  {
    id: "reportistica", labelKey: "menu.reportistica", icon: FileBarChart2, path: "/reportistica"
  },
  {
    id: "academy", labelKey: "menu.university", icon: GraduationCap,
    children: [
      { labelKey: "menu.training_course", path: "/academy/training", icon: PlayCircle },
      { labelKey: "menu.course_document", path: "/academy/documents", icon: BookOpen },
      { labelKey: "menu.knowledge_center", path: "/library/knowledge", icon: BookOpen },
      { labelKey: "menu.report_document", path: "/library/reports", icon: FileBarChart2 },
      { labelKey: "menu.repository", path: "/library/repository", icon: Archive },
    ]
  },
  {
    id: "legal", labelKey: "menu.legal", icon: Briefcase,
    children: [
      { labelKey: "menu.due_diligence", path: "/due-diligence", icon: Briefcase },
      { labelKey: "menu.contract_twin", path: "/contract-twin", icon: FileText },
      { labelKey: "menu.entity_loss", path: "/entity-loss-simulation", icon: AlertOctagon },
    ]
  },
  {
    id: "cyber_ops", labelKey: "menu.cyber_ops", icon: ShieldCheck,
    children: [
      { labelKey: "menu.cyber_exposure", path: "/cyber-exposure", icon: Radar },
      { labelKey: "menu.live_world", path: "/live-world", icon: Globe },
    ]
  },
  {
    id: "customer_support", labelKey: "menu.customer_support", icon: Headphones, path: "/customer-support"
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, tEn, lang, setLang } = useLanguage();
  const { enabled: enabledServices } = useServices();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({ sales: true });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Aperta di default su desktop, chiusa su mobile/tablet.
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });

  // Chiude automaticamente la sidebar quando si naviga su mobile/tablet.
  const closeSidebarMobile = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Filter menu items by admin-controlled service visibility.
  // Default to true if the service is not in the map (=admin hasn't touched it).
  const visibleMenuItems = menuItems.filter((it) => {
    if (it.id === "dashboard") return true; // dashboard always visible
    return enabledServices[it.id] !== false;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Poll unread count for Customer Support badge
  const [supportUnread, setSupportUnread] = useState(0);
  // Poll active alerts count for Contract Twin badge
  const [ctAlerts, setCtAlerts] = useState(0);
  // Poll watchlist alerts count for Entity Loss badge
  const [elAlerts, setElAlerts] = useState(0);
  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/support/unread-count`, { withCredentials: true });
        if (alive) setSupportUnread(data?.count || 0);
      } catch (err) { console.warn("[Layout] support unread-count failed:", err); }
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contract-twin/alerts/count`, { withCredentials: true });
        if (alive) setCtAlerts(data?.active || 0);
      } catch (err) { /* silent — Contract Twin optional */ }
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/entity-loss/watchlist/alerts/count`, { withCredentials: true });
        if (alive) setElAlerts(data?.count || 0);
      } catch (err) { /* silent */ }
    };
    fetch();
    const iv = setInterval(fetch, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Show a toast if a non-admin tried to access the admin area (set by AdminGuard).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("admin_blocked") === "1") {
        sessionStorage.removeItem("admin_blocked");
        toast.error("Accesso negato: area riservata agli amministratori.");
      }
      const svc = sessionStorage.getItem("service_blocked");
      if (svc) {
        sessionStorage.removeItem("service_blocked");
        toast.error(`Il servizio "${svc}" è temporaneamente disattivato.`);
      }
    } catch (err) {
      // sessionStorage unavailable — flash messages just don't show.
      console.warn("Layout: sessionStorage flash check failed", err);
    }
  }, []);

  const toggleMenu = (id) => {
    setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex h-screen bg-[#050505] overflow-hidden" onContextMenu={e => e.preventDefault()}>
      {/* Mobile topbar (lg:hidden) — hamburger + logo */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 flex items-center justify-between px-4 bg-[#0A0C10]">
        <button
          data-testid="mobile-menu-button"
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-[#CBD5E1] hover:text-white"
          aria-label="Apri menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <MoonstoneWordmark size="md" variant="monogram" />
        <button onClick={handleLogout} className="p-2 -mr-2 text-[#A0AEC0] hover:text-[#FF3B30]" aria-label="Logout">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Backdrop (mobile only when sidebar open) */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        />
      )}

      {/* Sidebar — off-canvas on mobile, inline on desktop */}
      <aside
        className={`${sidebarOpen ? "w-64 translate-x-0" : "lg:w-16 -translate-x-full lg:translate-x-0"} fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto flex-shrink-0 bg-[#0A0C10] flex flex-col transition-all duration-200 overflow-hidden`}
      >
        {/* Logo */}
        <div className={`flex items-center pt-8 pb-4 gap-3 ${sidebarOpen ? "px-4" : "px-0 justify-center"}`}>
          <button
            data-testid="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex-shrink-0 focus:outline-none"
            aria-label={sidebarOpen ? "Chiudi menu" : "Apri menu"}
            title={sidebarOpen ? "Chiudi menu" : "Apri menu"}
          >
            <MoonstoneWordmark size="lg" variant="monogram" />
          </button>
          {sidebarOpen && user?.client_name && (
            <div
              data-testid="license-holder-badge"
              className="flex-1 min-w-0 px-2.5 py-1.5 bg-[#0F131A]"
            >
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold leading-none">
                {t("common.license_holder")}
              </p>
              <p className="text-[12px] text-white font-semibold truncate leading-tight mt-0.5" title={user.client_name}>
                {user.client_name}
              </p>
            </div>
          )}
          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto flex-shrink-0 text-[#A0AEC0] hover:text-white hidden lg:block"
              aria-label="Chiudi menu"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-1">
          {visibleMenuItems.map(item => (
            <div key={item.id}>
              {item.external && item.path ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${item.id}`}
                  onClick={closeSidebarMobile}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white hover:bg-[#12161C] transition-all duration-200"
                >
                  <item.icon strokeWidth={1.5} className="w-[18px] h-[18px] flex-shrink-0 text-white/80 group-hover:text-white transition-colors" />
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left truncate">{tEn(item.labelKey)}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0 text-[#A0AEC0] group-hover:text-[#00E5FF]" />
                    </>
                  )}
                </a>
              ) : item.path ? (
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.id}`}
                  onClick={closeSidebarMobile}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "text-white bg-gradient-to-r from-[#00E5FF]/15 to-[#00E5FF]/5"
                        : "text-white hover:bg-[#12161C]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon strokeWidth={1.5} className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-[#00E5FF]" : "text-white/80 group-hover:text-white"}`} />
                      {sidebarOpen && <span className="truncate">{tEn(item.labelKey)}</span>}
                      {sidebarOpen && item.id === "customer_support" && supportUnread > 0 && (
                        <span data-testid="support-menu-badge" className="ml-auto text-[10px] font-bold tabular-nums bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {supportUnread}
                        </span>
                      )}
                      {sidebarOpen && item.id === "contract_twin" && ctAlerts > 0 && (
                        <span data-testid="contract-twin-menu-badge" className="ml-auto text-[10px] font-bold tabular-nums bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {ctAlerts}
                        </span>
                      )}
                      {sidebarOpen && item.id === "entity_loss" && elAlerts > 0 && (
                        <span data-testid="entity-loss-menu-badge" className="ml-auto text-[10px] font-bold tabular-nums bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {elAlerts}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ) : (
                <>
                  <button
                    data-testid={`nav-${item.id}`}
                    onClick={() => toggleMenu(item.id)}
                    className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white hover:bg-[#12161C] transition-all duration-200"
                  >
                    <item.icon strokeWidth={1.5} className="w-[18px] h-[18px] flex-shrink-0 text-white/80 group-hover:text-white transition-colors" />
                    {sidebarOpen && (
                      <>
                        <span className="flex-1 text-left truncate">{tEn(item.labelKey)}</span>
                        <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${openMenus[item.id] ? "rotate-90" : ""}`} />
                      </>
                    )}
                  </button>
                  {sidebarOpen && openMenus[item.id] && (
                    <div className="mt-1 ml-5 pl-3 space-y-0.5 animate-fadeIn">
                      {item.children.map(child => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={closeSidebarMobile}
                          data-testid={`nav-${child.path.replace(/\//g, "-").replace(/^-/, "")}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                              isActive
                                ? "text-[#00E5FF] bg-[#00E5FF]/10 font-semibold"
                                : "text-white hover:bg-[#12161C]"
                            }`
                          }
                        >
                          <child.icon strokeWidth={1.75} className="w-4 h-4 flex-shrink-0 text-white" />
                          {tEn(child.labelKey)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </nav>

        {/* User Footer */}
        <div className="p-3">
          {/* Language selector — sopra il nome utente */}
          {sidebarOpen ? (
            <div data-testid="lang-selector-inline" className="flex items-center gap-1 mb-2 border border-white/[0.04] p-0.5 bg-[#0F131A]" style={{ borderRadius: "2px" }}>
              {LANGS.map(l => (
                <button
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  title={l.name}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] tracking-widest uppercase font-semibold transition-colors ${
                    l.code === lang ? "bg-[#00E5FF]/12 text-[#00E5FF]" : "text-[#A0AEC0] hover:text-white"
                  }`}
                  style={{ borderRadius: "2px" }}
                >
                  <span className="text-sm leading-none">{l.flag}</span>
                  <span>{l.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 mb-2">
              {LANGS.map(l => (
                <button
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  title={l.name}
                  className={`text-xs px-1 py-0.5 ${l.code === lang ? "opacity-100" : "opacity-40 hover:opacity-100"}`}
                >
                  <span className="text-sm">{l.flag}</span>
                </button>
              ))}
            </div>
          )}
          {user?.role === "admin" && sidebarOpen && (
            <NavLink to="/admin" data-testid="admin-console-link"
              className="flex items-center gap-2 px-2 py-2 mb-2 rounded text-[10px] tracking-[0.2em] uppercase text-[#00E5FF] hover:bg-[#00E5FF]/10 transition">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
              Admin Console
            </NavLink>
          )}
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                <UserSearch className="w-3.5 h-3.5 text-[#00E5FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-[#A0AEC0] truncate">{user?.client_name}</p>
              </div>
              <button data-testid="theme-toggle" onClick={toggleTheme}
                title={theme === "dark" ? t("common.theme_light") : t("common.theme_dark")}
                className="text-[#CBD5E1] hover:text-[#00E5FF] transition-colors">
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button data-testid="logout-button" onClick={handleLogout} title={t("common.logout")} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <button data-testid="theme-toggle" onClick={toggleTheme}
                title={theme === "dark" ? t("common.theme_light") : t("common.theme_dark")}
                className="text-[#CBD5E1] hover:text-[#00E5FF] transition-colors py-1">
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button data-testid="logout-button" onClick={handleLogout} title={t("common.logout")} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors py-1">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="app-page-light flex-1 overflow-y-auto pt-14 lg:pt-0">
        {children}
      </main>
    </div>
  );
}
